import { GameLoop } from './game-loop.js';
import type {
  GameContext,
  InputFrame,
  Microgame,
  MicrogameResult,
  RunResult,
} from './types.js';

export interface FrameSource {
  getFrame(): InputFrame;
}

export interface RunnerOptions {
  microgames: Microgame[];
  context: GameContext;
  frameSource: FrameSource;
  /** Called between microgames so the shell can show a transition card. */
  onTransition?: (next: Microgame, prevResult: MicrogameResult | null) => void;
  /** Called when the whole run finishes. */
  onFinish?: (result: RunResult) => void;
  /** Optional overlay pass run AFTER each microgame's render. Used to paint
   *  the live face/hand/pose mesh on top of every microgame for free. */
  overlayDraw?: (
    ctx: CanvasRenderingContext2D,
    frame: InputFrame,
    requires: Microgame['requires'],
  ) => void;
}

/**
 * Plays a sequence of microgames back-to-back. Each microgame gets duration
 * seconds; when it ends the runner moves on. Tracks score across the run.
 */
export class GameRunner {
  private loop: GameLoop;
  private idx = 0;
  private elapsedInGame = 0;
  private current: Microgame | null = null;
  private results: MicrogameResult[] = [];
  private startTime = 0;
  private fpsEma: number | null = null;

  constructor(private opts: RunnerOptions) {
    this.loop = new GameLoop((dt) => this.tick(dt));
  }

  start(): void {
    this.idx = 0;
    this.results = [];
    this.startTime = performance.now();
    this.beginCurrent();
    this.loop.start();
  }

  stop(): void {
    this.loop.stop();
    this.current = null;
  }

  private beginCurrent(): void {
    this.elapsedInGame = 0;
    this.current = this.opts.microgames[this.idx] ?? null;
    if (this.current == null) {
      this.finish();
      return;
    }
    this.current.init(this.opts.context);
    this.opts.onTransition?.(this.current, this.results.at(-1) ?? null);
  }

  private tick(dt: number): void {
    if (this.current == null) return;
    this.elapsedInGame += dt;

    const frame = this.opts.frameSource.getFrame();
    this.current.tick(dt, frame);

    const { ctx, canvas } = this.opts.context;
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.current.render(ctx);
    ctx.restore();

    if (this.opts.overlayDraw != null) {
      ctx.save();
      this.opts.overlayDraw(ctx, frame, this.current.requires);
      ctx.restore();
    }

    // FPS overlay (EMA-smoothed). Drawn last so every microgame gets it.
    if (dt > 0.0005) {
      const inst = 1 / dt;
      this.fpsEma = this.fpsEma == null ? inst : this.fpsEma * 0.9 + inst * 0.1;
    }
    if (this.fpsEma != null) {
      ctx.save();
      const text = `${this.fpsEma.toFixed(0)} fps`;
      const size = Math.round(Math.max(11, canvas.height * 0.022));
      ctx.font = `${size}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      const m = ctx.measureText(text);
      const pad = 6;
      const x = canvas.width - 10;
      const y = canvas.height - 10;
      ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
      ctx.fillRect(x - m.width - pad * 2, y - size - pad, m.width + pad * 2, size + pad);
      ctx.fillStyle = '#9ef0ff';
      ctx.fillText(text, x - pad, y - pad / 2);
      ctx.restore();
    }

    if (this.elapsedInGame >= this.current.durationSec) {
      const result = this.current.end();
      this.results.push(result);
      this.idx += 1;
      this.beginCurrent();
    }
  }

  private finish(): void {
    this.loop.stop();
    const totalScore = this.results.reduce((s, r) => s + r.score, 0);
    const winCount = this.results.filter((r) => r.won).length;
    const durationSec = (performance.now() - this.startTime) / 1000;
    this.opts.onFinish?.({
      totalScore,
      winCount,
      microgames: this.results,
      durationSec,
    });
  }
}
